import CameraIcon from '@mui/icons-material/CameraAltOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormControl, FormHelperText, IconButton, Input, InputAdornment, InputLabel, SvgIconProps } from '@mui/material';
import { DesktopTooltip } from '@skoda/k2tools-app-components';
import * as React from 'react';
import ServicesContext from '../../../contexts/servicesContext';
import { VinValidationError } from '../../../enums/vinValidationError';
import { VinRecognitionResponse } from '../../../models';
import { ApiServiceError } from '../../../services/baseApiService';
import { VinService } from '../../../services/vinService';
import { formatFileSize } from '../../../utils/helpers';
import { Spinner } from '../spinner';
import styles from './styles.scss';

type VinInputProps = {
	onVinSubmit: (val: string, isValidFormat: boolean) => void;
	onVinSubmitError: () => void;
	defaultValue: string;
};

type VinInputState = {
	vinCode: string;
	error: string;
	isLoading: boolean;
	isFocus: boolean;
};

class VinInput extends React.Component<VinInputProps, VinInputState> {
	static contextType = ServicesContext;
	context: React.ContextType<typeof ServicesContext>;

	private readonly supportsInputCapture: boolean;

	constructor(props: VinInputProps) {
		super(props);

		this.state = {
			vinCode: null,
			error: null,
			isLoading: false,
			isFocus: false,
		};

		this.supportsInputCapture = this.getSupportsInputCapture();
	}

	componentWillReceiveProps(nextProps: VinInputProps) {
		if (!this.state.vinCode && this.props.defaultValue && nextProps.defaultValue) {
			this.setState({ vinCode: nextProps.defaultValue, isFocus: true });
		}
	}

	handleVinValidationProgress = (isLoading: boolean, hasManuals: boolean) => {
		if (isLoading) {
			this.setState({ isLoading: true, error: null });
		} else {
			this.setState({ isLoading: false, error: !hasManuals ? this.context.resourceProvider.getText('Form_Error_NoManuals') : null });
		}
	};

	handleVinClear = () => {
		this.setState({ vinCode: null, isFocus: false, error: null });
	};

	private getSupportsInputCapture = () => {
		const input = document.createElement('input');
		input.setAttribute('capture', 'environment');
		const supports = !!input['capture'];
		input.remove();
		return supports;
	};

	private getVinRecognitionErrorMessage = (error: unknown) => {
		if (error instanceof ApiServiceError) {
			const responseData = error.responseDataAs<VinRecognitionResponse>();
			switch (error.status) {
				case 204:
					return this.context.resourceProvider.getText('Form_Error_VinNotRecognized');
				case 400:
					return this.context.resourceProvider.getText('Form_Error_VinRecognitionInvalidFormat');
				case 408:
					return this.context.resourceProvider.getText('Form_Error_VinRecognitionTimeout');
				case 413: {
					let result = this.context.resourceProvider.getText('Form_Error_FileTooLarge');
					result = result.replace('{0}', formatFileSize(responseData?.metadata?.MaxFileSize));
					return result;
				}
				case 415: {
					let result = this.context.resourceProvider.getText('Form_Error_InvalidImageFormat');
					result = result.replace('{0}', responseData?.metadata?.AllowedFormats?.join(', '));
					return result;
				}
				case 422: {
					let result = this.context.resourceProvider.getText('Form_Error_ResolutionTooHigh');
					result = result.replace('{0}', `${responseData?.metadata?.MaxResolution}px`);
					return result;
				}
				default:
					return this.context.resourceProvider.getText('Form_Error_VinRecognitionFailed');
			}
		}

		return this.context.resourceProvider.getText('Form_Error_VinRecognitionFailed');
	};

	private processVinImage = (file: File) => {
		this.setState({ isLoading: true });
		this.context.apiService
			.recognizeVinFromImage(file)
			.then((data) => {
				if (!data.vin) {
					console.error('Received empty VIN:', data.message);
					const errorMessage = this.context.resourceProvider.getText('Form_Error_VinNotRecognized');
					this.setState({ isLoading: false, vinCode: '', error: errorMessage }, () => {
						this.props.onVinSubmit('', false);
					});
				} else {
					this.setState({ isLoading: false });
					this.processVinValueChange(data.vin);
				}
			})
			.catch((error) => {
				console.error('VIN recognition failed:', error.status ?? error, error.message ?? '');
				const errorMessage = this.getVinRecognitionErrorMessage(error);
				this.setState({ isLoading: false, vinCode: '', error: errorMessage }, () => {
					this.props.onVinSubmit('', false);
				});
			});
	};

	private handleVinImageInput = (type: 'upload' | 'camera') => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		if (type === 'camera') {
			input.capture = 'environment';
		}
		input.onchange = () => {
			const file = input.files.item(0);
			if (!file) {
				console.error('No file selected');
			} else {
				this.processVinImage(file);
			}
			input.remove();
		};
		input.click();
	};

	private processVinValueChange = (vinValue: string) => {
		if (!vinValue || vinValue.length === 0) {
			return this.setState({ vinCode: vinValue, error: null }, () => {
				this.props.onVinSubmit(vinValue, false);
			});
		}

		const result = VinService.validateVinCode(vinValue);
		if (result === VinValidationError.ERROR_INVALID_LENGTH) {
			this.setState({ vinCode: vinValue, error: this.context.resourceProvider.getText('Form_Error_VinLength') }, () => {
				this.props.onVinSubmit(vinValue, false);
			});
		} else if (result === VinValidationError.ERROR_INVALID_FORMAT) {
			this.setState({ vinCode: vinValue, error: this.context.resourceProvider.getText('Form_Error_InvalidFormat') }, () => {
				this.props.onVinSubmit(vinValue, false);
			});
		} else {
			this.setState({ vinCode: vinValue, error: null }, () => {
				this.props.onVinSubmit(vinValue, true);
			});
		}
	};

	private handleVinCodeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (this.state.isLoading) return;

		const vinValue = event.target.value.toUpperCase();
		this.processVinValueChange(vinValue);
	};

	private handleOnFocus = () => {
		this.setState({ isFocus: true });
		this.context.trackingPrepare.trackFocus();
	};

	private handleOnBlur = () => {
		this.setState({ isFocus: false });
	};

	private tooltipParagraph = (tKey: string) => {
		const text = this.context.resourceProvider.getText(tKey, '<empty>');
		if (text === '<empty>') return null;
		return <p className={styles['input-tooltip-paragraph']}>{text}</p>
	};

	render() {
		return (
			<FormControl fullWidth>
				<InputLabel />
				<Input
					className={styles['input-vin-code']}
					inputProps={{ maxLength: 17 }}
					value={this.state.vinCode ?? ''}
					onChange={this.handleVinCodeChange}
					onFocus={this.handleOnFocus}
					onBlur={this.handleOnBlur}
					endAdornment={
						<InputAdornment position='end'>
							{this.state.isLoading && <Spinner className={styles['input-spinner']} />}
							{!this.state.isLoading && !this.state.error && this.state.vinCode && <CheckIcon className={styles['input-adornment-icon']} />}
							{!this.state.isLoading && (
								<IconButton size='small' onClick={() => this.handleVinImageInput('upload')}>
									<ImageIcon />
								</IconButton>
							)}
							{this.supportsInputCapture && !this.state.isLoading && (
								<IconButton size='small' onClick={() => this.handleVinImageInput('camera')}>
									<CameraIcon />
								</IconButton>
							)}
						</InputAdornment>
					}
					startAdornment={
						!this.state.isFocus &&
						!this.state.vinCode && (
							<InputAdornment position='start'>
								<span className={styles['input-adornment-label']} style={{ pointerEvents: 'none' }}>
									{this.context.resourceProvider.getText('VIN_Code', 'VIN Code')}
								</span>
								<DesktopTooltip<SvgIconProps>
									AnchorComponent={InfoOutlinedIcon}
									AnchorComponentProps={{ color: 'primary', sx: { paddingLeft: '7px' } }}
								>
									{this.tooltipParagraph('VIN_code_tooltip')}
									{this.tooltipParagraph('VIN_code_tooltip_2')}
								</DesktopTooltip>
							</InputAdornment>
						)
					}
				/>
				<FormHelperText className={styles['input-vin-code-error']}>{this.state.error ?? ' '}</FormHelperText>
			</FormControl>
		);
	}
}

export default VinInput;
